import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { EditPencil } from "components/icons/EditPencil";
import { useFormikContext } from "formik";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useCreateTaxRateMutation,
  useGetTaxRatesQuery,
  useUpdateTaxRateMutation,
} from "store/apis/productAndServices";
import { TaxRate } from "types/Models/productAndServices";
import * as Yup from "yup";

const taxRateSchema = Yup.object({
  rate: Yup.number()
    .min(0, "Tax rate cannot be negative")
    .required("Tax rate is required"),
  description: Yup.string().max(100, "Maximum character limit is 100"),
});

type TaxRateValues = { rate: string; description: string };

type TaxRateComboboxProps = {
  name: string;
  label?: string;
  systemSuggestedRate?: number | null;
  size?: "small" | "regular";
};

const SYSTEM_SUGGESTED_OPTION_VALUE = "SYSTEM_SUGGESTED";

export const TaxRateCombobox = ({
  name,
  label = "Tax (%)",
  systemSuggestedRate,
  size = "regular",
}: TaxRateComboboxProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast } = useToast();

  const taxRateModal = useModal();
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRate | null>(null);

  const isEditing = Boolean(editingTaxRate);

  const handleClose = () => {
    taxRateModal.close();
    setEditingTaxRate(null);
  };

  const { data: taxRates = [], isLoading } = useGetTaxRatesQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const [createTaxRate, { isLoading: isCreating }] = useCreateTaxRateMutation();
  const [updateTaxRate, { isLoading: isUpdating }] = useUpdateTaxRateMutation();

  const { values, setFieldValue } = useFormikContext<Record<string, string>>();

  const savedOptions = taxRates.map(({ uuid, rate, description }) => ({
    label: `${rate}%`,
    value: uuid,
    data: description ?? "",
  }));

  const hasSystemSuggestedRate =
    systemSuggestedRate !== null && systemSuggestedRate !== undefined;
  const systemSuggestedOption = hasSystemSuggestedRate
    ? {
        label: `System suggested · ${systemSuggestedRate}%`,
        value: SYSTEM_SUGGESTED_OPTION_VALUE,
      }
    : null;

  const options = systemSuggestedOption
    ? [systemSuggestedOption, ...savedOptions]
    : savedOptions;

  const isSystemSuggestedSelected = hasSystemSuggestedRate && !values[name];

  const selectedOption = isSystemSuggestedSelected
    ? systemSuggestedOption
    : options.find((opt) => opt.value === values[name]);

  const handleSubmit = async ({ rate, description }: TaxRateValues) => {
    if (isEditing && editingTaxRate) {
      await updateTaxRate({
        groupId,
        entityId,
        taxRateId: editingTaxRate.uuid,
        payload: { rate: Number(rate), description },
      }).unwrap();
      successToast({ message: "Tax rate updated successfully" });
    } else {
      const newTaxRate = await createTaxRate({
        groupId,
        entityId,
        payload: { rate: Number(rate), description },
      }).unwrap();
      setFieldValue(name, newTaxRate.uuid);
      successToast({ message: "New tax rate added successfully" });
    }
    handleClose();
  };

  return (
    <>
      <Combobox
        label={label}
        size={size}
        menuPlacement="bottom"
        placeholder="Select"
        menuPortalTarget={document.body}
        name={name}
        options={options}
        value={selectedOption || null}
        onChange={(option) => {
          if (option instanceof Array) return;
          const value = option?.value || null;
          setFieldValue(
            name,
            value === SYSTEM_SUGGESTED_OPTION_VALUE ? null : value
          );
        }}
        isLoading={isLoading}
        isClearable={!hasSystemSuggestedRate}
        filterOption={({ label, data }, inputValue) => {
          const searchValue = inputValue.toLowerCase();
          const optionLabel = String(label).toLowerCase();
          const optionDescription = (data.data ?? "").toLowerCase();
          return (
            optionLabel.includes(searchValue) ||
            optionDescription.includes(searchValue)
          );
        }}
        actions={
          <Button
            customType="link"
            size="small"
            type="button"
            onClick={taxRateModal.open}
          >
            Add new tax rate
          </Button>
        }
        formatOptionLabel={(option, { context }) => {
          if (context === "value") return <span>{option.label}</span>;
          if (option.value === SYSTEM_SUGGESTED_OPTION_VALUE) {
            return <span>{option.label}</span>;
          }
          const taxRate = taxRates.find((t) => t.uuid === option.value);
          return (
            <div className="t-flex t-items-center t-justify-between t-w-full">
              <div className="t-flex t-flex-col t-min-w-0">
                <span>{option.label}</span>
                {taxRate?.description && (
                  <span className="t-text-text-30 t-text-body-sm t-break-words t-whitespace-normal">
                    {taxRate.description}
                  </span>
                )}
              </div>
              <ConditionalToolTip
                condition={!taxRate?.is_editable}
                text="Tax Rate cannot be edited since it is already associated with an active invoice"
              >
                <span>
                  <Button
                    customType="ghost_icon"
                    className="t-text-text-30 hover:t-text-text-60"
                    disabled={!taxRate?.is_editable}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!taxRate) return;
                      setEditingTaxRate(taxRate);
                      taxRateModal.open();
                    }}
                  >
                    <EditPencil />
                  </Button>
                </span>
              </ConditionalToolTip>
            </div>
          );
        }}
      />

      <Modal.Root open={taxRateModal.isOpen} onOpenChange={handleClose}>
        <Modal.Content size="regular" useCustomOverlay>
          <Modal.Header>
            <Modal.Title>
              {isEditing ? "Edit tax rate" : "Add new tax rate"}
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <FormikForm
            initialValues={{
              rate: isEditing ? String(editingTaxRate?.rate ?? "") : "",
              description: isEditing ? editingTaxRate?.description ?? "" : "",
            }}
            validationSchema={taxRateSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, values: formValues }) => (
              <>
                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <Input
                      withForm
                      label="Tax Rate"
                      required
                      name="rate"
                      type="number"
                      min={0}
                      step="any"
                      placeholder="Enter rate"
                      rightComponent={
                        <span className="t-text-body-sm t-text-text-60">%</span>
                      }
                    />
                    <Input
                      withForm
                      label="Description"
                      name="description"
                      placeholder="Enter description"
                    />
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button
                    type="button"
                    customType="secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    isLoading={isEditing ? isUpdating : isCreating}
                    disabled={
                      isSubmitting || (isEditing ? isUpdating : isCreating)
                    }
                  >
                    {isEditing ? "Save" : "Add"}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            )}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
